import { Injectable, inject } from '@angular/core';
import { environment } from '../../../environments/environment';
import {
  signIn,
  confirmSignIn,
  fetchAuthSession,
  signOut,
  AuthSession,
  ConfirmSignInOutput,
  resetPassword,
  confirmResetPassword
} from 'aws-amplify/auth';
import { Amplify } from 'aws-amplify';
import { SessionService } from './session.service';

@Injectable({ providedIn: 'root' })
export class AuthenticationService {

  private sessionService = inject(SessionService);

  constructor() {
    Amplify.configure({
      Auth: {
        Cognito: {
          userPoolId: environment.COGNITO_USERPOOL_ID,
          userPoolClientId: environment.COGNITO_APPCLIENT_ID,
        },
      },
    });
  }

  private isNamedError(error: unknown): error is { name: string } {
    return typeof error === 'object' && error !== null && 'name' in error;
  }

  private hasMessage(error: unknown): error is { message: string } {
    return typeof error === 'object' && error !== null && 'message' in error;
  }

    public async logIn(username: string, password: string): Promise<{ authState: 'loginSuccess' | 'newPasswordRequired' | 'mfaRequired' | 'emailCodeRequired' }> {
    try {
      const { isSignedIn, nextStep } = await signIn({ username, password });

      if (isSignedIn) {
        await this.setUserData();
        return { authState: 'loginSuccess' };
      }

      switch (nextStep.signInStep) {
        case 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED':
          return { authState: 'newPasswordRequired' };
        case 'CONFIRM_SIGN_IN_WITH_TOTP_CODE':
          return { authState: 'mfaRequired' };
        case 'CONFIRM_SIGN_IN_WITH_EMAIL_CODE': // CORRECCIÓN: Este es el valor correcto
          return { authState: 'emailCodeRequired' };
        default:
          throw new Error(`Unhandled sign-in step: ${nextStep.signInStep}`);
      }
    } catch (err) {
      console.error('AuthenticationService: Raw error from Amplify:', err);
      let newErr = new Error('Algo salió mal al iniciar sesión.');
      if (this.isNamedError(err)) {
        if (err.name === 'UserNotFoundException') {
          newErr = new Error('El usuario ingresado no existe.');
        } else if (err.name === 'NotAuthorizedException') {
          newErr = new Error('Contraseña o usuario incorrectos.');
        }
      }
      throw newErr;
    }
  }

  public async sendVerificationCode(code: string): Promise<void> {
    try {
      await confirmSignIn({ challengeResponse: code });
      await this.setUserData();
    } catch (err) {
      console.error('AuthenticationService: Raw error from confirmSignIn:', err);
      throw new Error(this.hasMessage(err) ? err.message : 'Código de verificación incorrecto.');
    }
  }

  public async confirmPassword(newPassword: string, name: string): Promise<ConfirmSignInOutput> {
    try {
      return await confirmSignIn({
        challengeResponse: newPassword,
        options: { userAttributes: { name } },
      });
    } catch (err) {
      let newErr = new Error('Algo salió mal al confirmar los datos.');
      if (this.hasMessage(err)) {
        if (err.message.includes('Password not long enough')) newErr = new Error('La contraseña no es válida. Debe ser más larga.');
        else if (err.message.includes('uppercase')) newErr = new Error('La contraseña debe contener mayúsculas.');
        else if (err.message.includes('numeric')) newErr = new Error('La contraseña debe contener números.');
        else if (err.message.includes('symbol')) newErr = new Error('La contraseña debe contener símbolos.');
      }
      throw newErr;
    }
  }

  public async forgotPassword(username: string): Promise<void> {
    try {
      await resetPassword({ username });
    } catch (err) {
      console.error('AuthenticationService: Raw error from resetPassword:', err);
      throw new Error(this.hasMessage(err) ? err.message : 'No se pudo iniciar el reseteo de contraseña.');
    }
  }

  public async confirmForgotPassword(username: string, confirmationCode: string, newPassword: string): Promise<void> {
    try {
      await confirmResetPassword({ username, confirmationCode, newPassword });
    } catch (err) {
      console.error('AuthenticationService: Raw error from confirmResetPassword:', err);
      throw new Error(this.hasMessage(err) ? err.message : 'No se pudo confirmar la nueva contraseña.');
    }
  }

  public async isLoggedIn(): Promise<boolean> {
    try {
      const session: AuthSession = await fetchAuthSession();
      return session.tokens !== undefined;
    } catch (err) {
      return false;
    }
  }

  public async getAccessToken(): Promise<string | null> {
    try {
      const session = await fetchAuthSession({ forceRefresh: false });
      return session.tokens?.accessToken.toString() ?? null;
    } catch (err) {
      return null;
    }
  }

  public async getIdToken(): Promise<string | null> {
    try {
      const session = await fetchAuthSession({ forceRefresh: false });
      return session.tokens?.idToken?.toString() ?? null;
    } catch (err) {
      return null;
    }
  }

  public async logOut(): Promise<void> {
    try {
      await signOut();
    } finally {
      // Tokens gestionados por Amplify; solo limpiamos el userId propio.
      // Limpia claves legacy de versiones anteriores.
      localStorage.removeItem('accessToken');
      localStorage.removeItem('idToken');
      localStorage.removeItem('userId');
      this.sessionService.setCurrentUser(null);
    }
  }

  private async setUserData(): Promise<void> {
    const session = await fetchAuthSession();
    if (session.tokens && session.tokens.idToken) {
      // No se almacenan tokens en localStorage (riesgo XSS): Amplify
      // gestiona la sesión y el interceptor los obtiene vía fetchAuthSession.
      // El cambio de userId dispara (vía SessionService signal) la recarga
      // de roles en PermissionService para recalcular permisos.
      const userId = session.tokens.idToken.payload['cognito:username'] as string;
      this.sessionService.setCurrentUser(userId);
    }
  }
}